/**
 * @fileoverview Actual GPS travel route viewer (polyline of raw tracked points)
 * @module components/maps/TravelRouteMap
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { t } from '../CustomComponent';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_LOADER_ID } from '../../utils/googleMaps';


const DEFAULT_CONTAINER_STYLE = { width: '100%', height: '65vh' };
const DEFAULT_CENTER = { lat: 13.7563, lng: 100.5018 };

const MAP_OPTIONS = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
};

const POLYLINE_OPTIONS = {
  strokeColor: '#FF6B00',
  strokeOpacity: 0.9,
  strokeWeight: 4,
  geodesic: true,
};

/**
 * TravelRouteMap - วาดเส้นทางที่ช่างเดินทางจริงจากจุด GPS ดิบ (ไม่ผ่าน Directions API)
 * จึงรองรับจำนวนจุดได้ไม่จำกัดตามข้อจำกัด waypoint ของ Directions
 *
 * @param {Object} props
 * @param {Array} props.points - จุดเส้นทางเรียงตามเวลา [{ lat, lng, at? }, ...]
 * @param {Object} [props.containerStyle] - style ของกล่องแผนที่
 * @param {string} [props.googleMapsApiKey]
 * @returns {React.ReactElement}
 */
const TravelRouteMapInner = ({
  points = [],
  containerStyle = DEFAULT_CONTAINER_STYLE,
  googleMapsApiKey = GOOGLE_MAPS_API_KEY,
  onRetry,
}) => {
  const mapRef = useRef(null);
  const [authFailed, setAuthFailed] = useState(false);

  // Google เรียก gm_authFailure เมื่อคีย์ถูกปฏิเสธ (restriction/billing/API ไม่เปิด) - แผนที่จะขึ้นหน้า "Oops" ของ Google
  useEffect(() => {
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => {
      setAuthFailed(true);
      if (typeof previous === 'function') previous();
    };
    return () => {
      window.gm_authFailure = previous;
    };
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // จุดที่ใช้ได้จริง (ตัดค่าที่ไม่ใช่ตัวเลขทิ้ง) - ลำดับคือลำดับเวลาที่ backend เรียงมาให้แล้ว
  const path = useMemo(
    () =>
      (points || [])
        .filter((point) => point && Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng)))
        .map((point) => ({ lat: Number(point.lat), lng: Number(point.lng) })),
    [points]
  );

  const center = path.length ? path[Math.floor(path.length / 2)] : DEFAULT_CENTER;

  // ซูมให้เห็นทั้งเส้นทางพอดีตอนแผนที่พร้อม
  const handleLoad = useCallback(
    (map) => {
      mapRef.current = map;
      if (!path.length || !window.google) return;
      if (path.length === 1) {
        map.setCenter(path[0]);
        map.setZoom(16);
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      path.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds, 48);
    },
    [path]
  );

  // ยังไม่ได้ตั้งค่าคีย์ Google Maps - บอกให้ชัดแทนที่จะหมุนโหลดค้าง
  if (!googleMapsApiKey) {
    return (
      <div className="d-flex align-items-center justify-content-center bg-light text-secondary p-4" style={containerStyle}>
        REACT_APP_API_MAP_KEY is not configured
      </div>
    );
  }

  if (loadError || authFailed) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center bg-light text-center p-4"
        style={containerStyle}
      >
        <div className="text-danger mb-3">{t('map_load_failed')}</div>
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          {t('map_retry')}
        </button>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="d-flex align-items-center justify-content-center bg-light" style={containerStyle}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={13} options={MAP_OPTIONS} onLoad={handleLoad}>
      {path.length > 1 && <Polyline path={path} options={POLYLINE_OPTIONS} />}

      {/* จุดกลางทางแต่ละจุดที่อุปกรณ์บันทึกไว้ - วงเล็ก ๆ เพื่อให้เห็นความถี่ของการเก็บพิกัด */}
      {path.slice(1, -1).map((point, index) => (
        <Marker
          key={`route-point-${index}`}
          position={point}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 4,
            fillColor: '#FF6B00',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 1,
          }}
          title={points[index + 1]?.at || ''}
        />
      ))}

      {path.length > 0 && (
        <Marker
          position={path[0]}
          label={{ text: 'A', color: 'white', fontWeight: 'bold' }}
          title={points[0]?.at || 'Start'}
        />
      )}
      {path.length > 1 && (
        <Marker
          position={path[path.length - 1]}
          label={{ text: 'B', color: 'white', fontWeight: 'bold' }}
          title={points[points.length - 1]?.at || 'End'}
        />
      )}
    </GoogleMap>
  );
};

// ลองใหม่ = ลบ script เดิมที่ค้าง/พัง แล้ว mount ตัวโหลดใหม่ให้ฉีด script อีกรอบ
const TravelRouteMap = (props) => {
  const [attempt, setAttempt] = useState(0);

  const handleRetry = useCallback(() => {
    const stale = document.getElementById(GOOGLE_MAPS_LOADER_ID);
    if (stale) stale.remove();
    if (window.google && !window.google.maps) delete window.google;
    setAttempt((value) => value + 1);
  }, []);

  return <TravelRouteMapInner key={attempt} {...props} onRetry={handleRetry} />;
};

TravelRouteMap.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      at: PropTypes.string,
    })
  ),
  containerStyle: PropTypes.object,
  googleMapsApiKey: PropTypes.string,
};

export default TravelRouteMap;
